import { Controller } from "stimulus";
import OptionalServices from "../../helpers/program_offers/optional_services_helper";
import TargetHelper from "../../helpers/target_helpers";

const OPE = "optional_payment_election"
/**
 * Handles terms selection via additional programs (Lease Details tab)
 */
export default class extends Controller {
  static targets = [
    "invoiceForm",
    "merchandiseTotalInput",
    "offerBox",
    "offerBoxSelection",
    "optionalPaymentAmount",
    "optionalPaymentSelectedPaymentType",
    "paymentOptionBox",
    "paymentOptionsContainer",
    "repaymentAmount",
    "selectablePaymentOptions",
    "selectablePaymentOptionsContainer",
  ]

  connect(){
    this.targetHelper = new TargetHelper(this);

    if (this.invoicesController.merchandiseTotalReady) {
      this.handleEdit();
    }
  }

  merchandiseTotalBlur(){
    const maximumMerchandiseTotal = parseFloat(
      this.invoiceFormTarget.dataset[
        "users-Contracts-InvoicesMaximumMerchandiseTotal"
      ]
    );

    const minimumMerchandiseTotal = parseFloat(
      this.invoiceFormTarget.dataset[
        "users-Contracts-InvoicesMinimumMerchandiseTotal"
      ]
    );

    if (
      this.merchandiseTotalInputTarget.value >= minimumMerchandiseTotal &&
      this.merchandiseTotalInputTarget.value <= maximumMerchandiseTotal
    ) {
      if(this.optionalPaymentSelectedPaymentType === OPE) this.resetOptionalPaymentFields();

      this.targetHelper.removeClassTarget("offerBox", "disabled", true);
      if (this.selectedProgram) this.renderProgramOptions();
    } else {
      if (this.optionalPaymentSelectedPaymentType === OPE) return;

      this.targetHelper.addClassTarget("offerBox", "disabled", true);
    }
  }

  resetOfferBoxes(){
    this.targetHelper.removeClassTarget("offerBoxSelection", "has-success");
    this.targetHelper.removeClassTarget("offerBox", "selected", true);

    this.invoicesController.underwritingProgramIdTarget.value = "";
    this.invoicesController.updateInvoiceFormStatus();
  }

  handleEdit(){
    if (!this.targetHelper.isPresent("offerBoxSelection")) return

    this.merchandiseTotalBlur();
    const { selectedProgramType, selectedProgramId } =
      this.offerBoxSelectionTarget.dataset;
    const offerBoxTarget = this.offerBoxTargets.find(
      (offerBoxTarget) =>
        offerBoxTarget.dataset.programType === selectedProgramType
    );
    this.targetHelper.addClassTarget("offerBoxSelection", "has-success")
    offerBoxTarget.classList.add("selected");
    this.invoicesController.underwritingProgramIdTarget.value =
      selectedProgramId;
    this.selectedTerm.classList.add("selected");
    this.paymentOptionsContainerTarget.classList.add("has-success");
    this.reCalculateOptionalServices();
  }

  handleSelectOffer(event) {
    event.preventDefault();

    this.selectOffer(event.currentTarget);
  }

  selectOffer(offerBoxTarget){
    if (
      offerBoxTarget.classList.contains("disabled") ||
      offerBoxTarget.classList.contains("selected")
    ) {
      return;
    }

    this.resetOfferBoxes();

    this.targetHelper.addClassTarget("offerBoxSelection", "has-success");
    offerBoxTarget.classList.add("selected");

    this.renderProgramOptions();
  }

  renderProgramOptions(){
    const merchandiseTotal = parseFloat(this.merchandiseTotalInputTarget.value);
    const { programType } = this.selectedProgram.dataset;
    const { programOptionsPath } = this.offerBoxSelectionTarget.dataset

    const params = new URLSearchParams({
      program_type: programType,
      merchandise_total: merchandiseTotal,
    });

    if (this.optionalPaymentAmount) params.append('selected_optional_payment_amount', this.optionalPaymentAmount);
    if (this.optionalPaymentSelectedPaymentType) params.append('selected_optional_payment_type', this.optionalPaymentSelectedPaymentType);

    const url = `${programOptionsPath}?${params.toString()}`;

    fetch(url, window.acima.fetchInit({ method: "GET" }))
      .then((response) => response.text())
      .then((html) => this.selectablePaymentOptionsTarget.innerHTML = html)
      .then(() => this.targetHelper.removeClassTarget("paymentOptionsContainer", "hidden"))
      .then(() => this.reSelectTermLength())
      .then(() => this.reCalculateOptionalServices())
      .catch((error) => console.error("Error fetching partial:", error));
  }

  handleSelectTermLength(event){
    if (event.currentTarget.classList.contains("selected")) return;

    this.selectTermLength(event.currentTarget);
  }

  selectTermLength(termOptionTarget) {
    this.targetHelper.removeClassTarget("paymentOptionBox", "selected", true)
    termOptionTarget.classList.add("selected");
    this.invoicesController.underwritingProgramIdTarget.value = termOptionTarget.dataset["underwritingProgramId"];
    this.targetHelper.addClassTarget("paymentOptionsContainer", "has-success")
    this.invoicesController.updateInvoiceFormStatus();
    this.reCalculateOptionalServices();
  }

  reSelectTermLength(){
    if (!this.selectedTerm) return;

    this.selectTermLength(this.selectedTerm);
  }

  reCalculateOptionalServices(){
    const optionalServices = new OptionalServices(this);

    optionalServices.calculateOptionalServiceAmounts();
  }

  resetOptionalPaymentFields(){
    if(this.targetHelper.isPresent('optionalPaymentSelectedPaymentType')) {
      return this.optionalPaymentSelectedPaymentTypeTarget.value = ""
    }

    if(this.targetHelper.isPresent('optionalPaymentAmount')) {
      return this.optionalPaymentAmountTarget.value = "";
    }
  }

  updateRetailerInvoiceTotal(){
    this.targetHelper.removeClassTarget("offerBox", "disabled", true);
    if (this.selectedProgram) this.renderProgramOptions(this.selectedProgram)

    this.reCalculateOptionalServices();
  }

  /**
   * Returns optional payment amount value
   * @return {String} Optional Payment amount value.
  */
  get optionalPaymentAmount(){
    if (this.hasOptionalPaymentAmountTarget){
      return this.optionalPaymentAmountTarget.value;
    } else {
      return ""
    }
  }

  /**
   * Returns optionalPaymentSelectedType value
   * @return {String} Optional Payment selected type value.
  */
  get optionalPaymentSelectedPaymentType(){
    if(this.hasOptionalPaymentSelectedPaymentTypeTarget){
      return this.optionalPaymentSelectedPaymentTypeTarget.value;
    } else {
      return "";
    }
  }

  /**
   * Get selectedProgram (promo only)
   * @return {Element|undefined} HTML element if selected, null otherwise.
   */
  get selectedProgram(){
    if(!this.targetHelper.isPresent('offerBox')) return;

    return this.offerBoxTargets.find((offerBoxTarget)=> {
      return offerBoxTarget.classList.contains('selected')
    })
  }

  /**
   * Get selectedTerm (Every Other Week Recurring Payment)
   * @return {Element|undefined} HTML element if selected, null otherwise.
   */
  get selectedTerm(){
    if (!this.targetHelper.isPresent("offerBoxSelection")) return;
    if (
      this.underwritingProgramId.length === 0 &&
      this.offerBoxSelectionTarget.dataset.selectedProgramId.length === 0
    )
      return;

    const underwritingProgramId =
      this.underwritingProgramId ||
      this.offerBoxSelectionTarget.dataset.selectedProgramId;

    return this.paymentOptionBoxTargets.find((paymentOptionBoxTarget) => {
      return (
        paymentOptionBoxTarget.dataset.underwritingProgramId ===
        underwritingProgramId
      );
    });
  }

  /**
   * Returns underwritingProgramId value from InvoicesController
   * @return {String} Underwriting program id value.
  */
  get underwritingProgramId(){
    if(!this.invoicesController.hasUnderwritingProgramIdTarget) return;

    return this.invoicesController.underwritingProgramIdTarget.value;
  }

  /**
   * Returns InvoicesController reference
   * @return {Object} InvoicesController reference.
  */
  get invoicesController(){
    return this.application.controllers.find( (controller) => {
      return controller.identifier.toString() == "users--contracts--invoices";
    });
  }
}
